import {
    AddOrderWatchersCommandInput,
    GetOrders_AllCommandInput,
    GetOrdersResponseContent,
    ITO,
    PostItemAttachmentCommandInput,
    PostItemAttachmentCommandOutput,
    PostOrders_AllCommandInput,
    PostOrdersAllResponseContent,
    SubmitOrderCommandInput,
    SubmitOrderCommandOutput,
} from "@amzn/ito-client";
import { SentryFetchHttpHandler } from "@amzn/sentry-fetch-http-handler";
import { ORDERING_CONFIG } from "configuration/config";
import { IOrdersService } from "./i-orders-service";
import { detailsStructure, ITOConsumer } from "interfaces/itoTypes";
import { CSRFService } from "services/csrf-service";
import { getString } from "common/ui_string_labels/ui-strings-utils";
import { buildingFormat } from "interfaces";
import { UserPermissions } from "services/user-permissions";
import { lookup } from "mime-types";

const getStringPath = "errors.ito";

/**
 * Mapping of ITO consumers to their respective proxy endpoints
 *
 * @constant
 * @type {Object.<ITOConsumer, string>}
 */
const proxyMapping: { [key in ITOConsumer]: string } = {
    ham: "/ito-ham",
    amznham: "/ito-amznham",
};

/**
 * CSRF Token Header key
 *
 * @constant
 * @type {string}
 */
const CSRF_TOKEN_HEADER: string = "x-csrf-token";

/**
 * Default value for the firstEdit field in ITO order requests
 *
 * @constant
 * @type {ITOConsumer}
 */
const FIRST_EDIT_VALUE: ITOConsumer = "ham";

/**
 * Converts detailsStructure to DocumentType format for ITO API compatibility
 *
 * @param {detailsStructure} details - The details structure containing order information
 * @return {DocumentType} - Formatted document type object with ISO date strings and null handling
 */
export function formatDetailsToDocumentType(details: detailsStructure): Record<string, any> {
    return {
        requestType: details.requestType,
        isExpedite: details.isExpedite ?? null,
        costCenter: details.costCenter,
        projectCode: details.projectCode ?? null,
        financialCode: details.financialCode,
        glCode: details.glCode,
        businessJustification: details.businessJustification,
        locationGlCode: details.locationGlCode,
        expediteDate: details.expediteDate?.toISOString() ?? null,
        requestCategories: details.requestCategories ?? null,
    };
}

/**
 * Service for handling ITO operations
 *
 * @class
 * @implements {IOrdersService}
 * @property {ITO} client - ITO client instance for API communication
 * @property {SentryFetchHttpHandler} requestHandler - HTTP request handler with Sentry integration
 */
export class ITOOrdersService implements IOrdersService {
    private client: ITO;
    private requestHandler: SentryFetchHttpHandler;
    private csrfService: CSRFService;
    private consumer: ITOConsumer;

    /**
     * Creates an instance of ITOOrdersService.
     *
     * @param {ITOConsumer} [consumer] - The ITO consumer type, defaults to "ham"
     */
    constructor(consumer?: ITOConsumer) {
        this.requestHandler = new SentryFetchHttpHandler();

        this.consumer = consumer ?? "ham";

        this.client = new ITO({
            endpoint: ORDERING_CONFIG.itoEndpoint + proxyMapping[this.consumer],
            region: "*",
            credentials: { accessKeyId: "", secretAccessKey: "" },
            requestHandler: this.requestHandler,
        });

        this.csrfService = new CSRFService();
    }

    /**
     * Creates a new order with the provided request content and CSRF token.
     * Formats the details and deliveryLocation to match ITO API compatibility
     *
     * @param {SubmitOrderCommandInput} request - The order request content containing order details
     * @param {string} csrfToken - CSRF token for security validation
     * @return {Promise<SubmitOrderCommandOutput>} - Promise that resolves to the created order response content
     */
    public async CreateOrder(
        request: SubmitOrderCommandInput,
        csrfToken: string,
        details: detailsStructure,
        deliveryLocation: buildingFormat,
    ): Promise<SubmitOrderCommandOutput> {
        this.requestHandler.pushHeader(CSRF_TOKEN_HEADER, csrfToken);

        const formDetails = formatDetailsToDocumentType(details);

        const deliveryDetails = deliveryLocation as Record<string, any>;

        request.details = {
            ...formDetails,
            deliverTo: deliveryDetails,
            firstEdit: FIRST_EDIT_VALUE,
        };

        const order = await this.client.submitOrder(request);

        return order;
    }

    /**
     * Retrieves a security token for CSRF protection
     *
     * @return {Promise<string>} - Promise resolving to the CSRF security token
     */
    public async GetSecurityToken(): Promise<string> {
        return this.csrfService.getToken();
    }

    /**
     * Uploads a file attachment for an order item with the selected taxonomy
     *
     * @param {string} taxonomyId - The taxonomy identifier for the order item
     * @param {File} attachment - The file to be uploaded as an attachment
     * @param {string} contentType - The type of the file content
     * @param {string} fileName - The name of the file being uploaded
     * @param {string} csrfToken - The CSRF token
     *
     * @return {Promise<PostItemAttachmentCommandOutput>} - Promise that returns the attachmentId
     */
    public async uploadOrderFile(
        taxonomyId: string,
        attachment: File,
        contentType: string,
        fileName: string,
        csrfToken: string,
    ): Promise<PostItemAttachmentCommandOutput> {
        if (!contentType) {
            contentType = lookup(fileName) || "";
        }

        if (!attachment) {
            throw new Error(getString(`${getStringPath}.invalidFile`));
        }

        const arrayBuffer = await attachment.arrayBuffer();
        const attachmentFile = new Uint8Array(arrayBuffer);

        this.requestHandler.pushHeader("x-csrf-token", csrfToken);

        const uploadedBy = UserPermissions.instance.getUsername;

        const encodedFileName = encodeURIComponent(fileName.substring(0, fileName.lastIndexOf(".")) || fileName);

        const request: PostItemAttachmentCommandInput = {
            taxonomyId,
            attachment: attachmentFile,
            contentType,
            fileName: encodedFileName,
            uploadedBy,
        };

        try {
            const response = await this.client.postItemAttachment(request);

            if (!response) {
                throw new Error(getString(`${getStringPath}.noResponse`));
            }

            if (!response.attachmentId) {
                throw new Error(getString(`${getStringPath}.invalidResponse`));
            }
            return response;
        } catch (error) {
            throw new Error(getString(`${getStringPath}.uploadFile`));
        }
    }

    /**
     * Retrieves all orders based on the provided input parameters
     *
     * @param {GetOrders_AllCommandInput} props - The input parameters for retrieving orders
     * @return {Promise<GetOrdersResponseContent>} - Promise that resolves to the orders response content
     */
    public async getOrders(props: GetOrders_AllCommandInput): Promise<GetOrdersResponseContent> {
        try {
            const orders = await this.client.getOrders_All(props);
            return orders;
        } catch (error) {
            throw new Error(getString(`${getStringPath}.getOrders`));
        }
    }

    /**
     * Retrieves all orders based on the provided input parameters
     *
     * @param {PostOrders_AllCommandInput} props - The input parameters for retrieving orders
     * @return {Promise<PostOrdersAllResponseContent>} - Promise that resolves to the orders response content
     */
    public async getCustomQueryOrders(props: PostOrders_AllCommandInput): Promise<PostOrdersAllResponseContent> {
        try {
            // Get CSRF token for POST request
            const csrfToken = await this.GetSecurityToken();
            this.requestHandler.pushHeader(CSRF_TOKEN_HEADER, csrfToken);

            const orders = await this.client.postOrders_All(props);
            return orders;
        } catch (error) {
            throw new Error(getString(`${getStringPath}.postOrdersAll`));
        }
    }

    /**
     * Add a new watcher to order
     *
     * @param {PostOrders_AllCommandInput} props - The input parameters for retrieving orders
     * @return {Promise<PostOrdersAllResponseContent>} - Promise that resolves to the orders response content
     */
    public async addOrderWatchers(orderId: string, watcher: string, csrfToken: string) {
        const request: AddOrderWatchersCommandInput = {
            orderId,
            watcher
        };
        try {
            const response = await this.client.addOrderWatchers(request);
            return response;
        } catch (err) {
            throw new Error(getString(`${getStringPath}.addOrderWatchers`));
        }
    }
}
